import React, { useEffect, useId, useState } from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import { Box, ButtonBase, Collapse, Skeleton, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { FONT_STACK_SUISSE_INTL, FONT_STACK_SUISSE_WORKS } from '~/components/brand/brandTokens';

import type {
  AnalyticsMeasureSums,
  AnalyticsMetricResult,
  AnalyticsSparklineTrend,
  AnalyticsTrendPoint,
  CommercialMetricId,
} from '~/types/leLens';

import { useAreaFit } from '../../areas/AreaFitContext';
import MoreMetricsToggle from '../../areas/MoreMetricsToggle';
import { moreMetricsState } from '../../areas/moreMetricsState';
import { STRIP_CONTAINER_SX, stripGridSx } from '../../areas/stripGrid';

import Sparkline from './Sparkline';
import {
  formatMetric,
  formatMetricDelta,
  formatRangeLabel,
  isFavourable,
  metricValue,
  sparklineSeries,
} from './derive';
import { analyticsTitle } from './textCasing';

type SparklineTrend = AnalyticsSparklineTrend<AnalyticsTrendPoint>;

type MetricStripProps = {
  metrics: ReadonlyArray<AnalyticsMetricResult>;
  sparklineTrend: SparklineTrend | undefined;
  focusMetric: CommercialMetricId;
  /** False while the chart is put away, so no tile reads as selected. */
  focusVisible?: boolean;
  onFocusChange: (id: CommercialMetricId) => void;
  /** "vs last year" / "vs previous period", precomputed by the caller from the resolved comparison. */
  comparisonLabel: string;
  onAsk: (metricId: CommercialMetricId) => void;
  onOpenDetails: (metricId: CommercialMetricId) => void;
  onStartLens: (metricId: CommercialMetricId) => void;
  allowOverflow?: boolean;
  /** Cells in the headline row; the rest wait in the overflow row. Defaults to six. */
  headlineCount?: number;
  loading?: boolean;
  /** The strip card's last row. Gets the "More metrics" toggle to place in its right slot. */
  footer?: (moreMetrics: React.ReactNode) => React.ReactNode;
  /** Opens under the footer, inside the card. */
  panel?: React.ReactNode;
};

const CELL_COUNT = 6;
/** Rows draw their own hairlines inside cells; the grid reaches past the frame by that hairline so the last column and row draw no double edge. */
const GRID_BLEED_SX = { mb: '-1px', mr: '-1px' } as const;
/** Below this tile width the figure uses the compact size. */
const COMPACT_TILE_WIDTH = 210;
/** The sparkline's height: full design at fit level 0, a little shorter through levels 1 and 2, trimmed at level 3. */
const ROOMY_SPARKLINE_HEIGHT_PX = 30;
const SPARKLINE_HEIGHT_PX = 28;
const FITTED_SPARKLINE_HEIGHT_PX = 22;

function cardSx(tokens: ReturnType<typeof lensTokens>) {
  return {
    backgroundColor: tokens.surface,
    border: `1px solid ${alpha(tokens.accent, 0.16)}`,
    borderRadius: '10px',
  };
}

function stripDividerColor(tokens: ReturnType<typeof lensTokens>): string {
  return alpha(tokens.accent, 0.12);
}

/** Length tiers the headline figure steps down through so a long formatted value ("$2,468.61")
 *  never gets ellipsis-clipped: at `compact === false` (the strip's normal width) 34px up to 8
 *  characters, 30px at 9-10, 27px at 11+; `compact === true` (a tile narrower than `COMPACT_TILE_WIDTH`)
 *  starts from 28px instead of 34px, with the same two step-downs (4px, then a further 3px)
 *  applied relatively, giving 28/24/21. The figure itself must never truncate: this is a fallback
 *  that keeps the common case readable, not a substitute for `overflow: visible` on the element. */
export function valueFontSize(value: string, compact: boolean): number {
  const base = compact ? 28 : 34;
  if (value.length <= 8) {
    return base;
  }
  if (value.length <= 10) {
    return base - 4;
  }
  return base - 7;
}

const RATIO_METRIC_IDS: ReadonlySet<CommercialMetricId> = new Set([
  'commercial.margin_pct_br',
  'commercial.margin_pct_ar',
  'commercial.aov_br',
  'commercial.aov_ar',
  'commercial.adr_br',
  'commercial.adr_ar',
  'commercial.avg_package_value_br',
  'commercial.avg_package_value_ar',
  'commercial.gm_per_package_br',
  'commercial.gm_per_package_ar',
  'commercial.ttv_per_pax_br',
  'commercial.ttv_per_pax_ar',
  'commercial.gm_per_pax_br',
  'commercial.gm_per_pax_ar',
  'commercial.ttv_per_cruise_night_br',
  'commercial.ttv_per_cruise_night_ar',
  'commercial.refund_rate',
  'commercial.refunds_pct_gm',
  'commercial.refunds_pct_gm_overrides',
  'commercial.ttv_per_room_night_br',
  'commercial.ttv_per_room_night_ar',
  'commercial.gm_per_room_night_br',
  'commercial.gm_per_room_night_ar',
  'commercial.revenue_per_view_br',
  'commercial.revenue_per_view_ar',
  'commercial.margin_per_view_br',
  'commercial.margin_per_view_ar',
]);

/** Plain-language time context for the otherwise axis-free KPI sparkline. */
export function sparklineContextLabel(trend: Pick<SparklineTrend, 'grain' | 'window' | 'rollingWindowDays'>): string {
  const range = formatRangeLabel(trend.window.from, trend.window.to) ?? trend.window.label;
  const grain = trend.grain === 'day' ? 'Daily' : trend.grain === 'week' ? 'Weekly' : 'Monthly';
  return `${grain} · ${range}${trend.rollingWindowDays ? ` · ${trend.rollingWindowDays}-day rolling` : ''}`;
}

/** Compact cell label for the one Accommodation efficiency metric whose governed name is too
 * long for a six-up KPI strip. The full governed label remains in the accessible name and title. */
function metricCellLabel(metric: AnalyticsMetricResult): string {
  return analyticsTitle(metric.shortName || metric.name);
}

/**
 * The sparkline series for one metric, already correctly smoothed. Additive metrics (TTV, gross
 * margin, orders, refunds) hand back their raw per-bucket values with `window` untouched:
 * `Sparkline` itself computes the rolling mean of those. Ratio metrics (Margin %, AOV, Refund
 * rate) are smoothed here instead, as a rolling sum of the numerator over a rolling sum of the
 * denominator across the same `window` of buckets, because averaging the daily ratio is a
 * different (and wrong) number from the ratio of the summed sums over those days; the returned
 * `smoothWindow` is then 1, so `Sparkline` does not smooth an already-smoothed series again.
 */
export function metricSparklineSeries(
  points: ReadonlyArray<AnalyticsTrendPoint>,
  id: CommercialMetricId,
  window: number,
): { values: Array<number | null>; smoothWindow: number } {
  if (!RATIO_METRIC_IDS.has(id)) {
    return { values: sparklineSeries(points, id), smoothWindow: window };
  }
  const values = points.map((_, index) => {
    const start = Math.max(0, index - window + 1);
    const sums: AnalyticsMeasureSums = {
      gross_sales_aud: 0,
      margin_aud: 0,
      accounting_refund_amount_aud: 0,
      margin_aud_refund: 0,
      order_bk: 0,
      packages: 0,
      room_nights: 0,
      cruise_nights: 0,
      total_pax: 0,
    };
    for (let i = start; i <= index; i += 1) {
      const point = points[i].current;
      sums.gross_sales_aud += point.gross_sales_aud;
      sums.margin_aud += point.margin_aud;
      sums.accounting_refund_amount_aud += point.accounting_refund_amount_aud;
      sums.margin_aud_refund = (sums.margin_aud_refund ?? 0) + (point.margin_aud_refund ?? 0);
      sums.order_bk += point.order_bk;
      sums.packages = (sums.packages ?? 0) + (point.packages ?? 0);
      sums.room_nights = (sums.room_nights ?? 0) + (point.room_nights ?? 0);
      sums.cruise_nights = (sums.cruise_nights ?? 0) + (point.cruise_nights ?? 0);
      sums.total_pax = (sums.total_pax ?? 0) + (point.total_pax ?? 0);
    }
    return metricValue(id, sums);
  });
  return { values, smoothWindow: 1 };
}

/**
 * The strip's ranking rule, pulled out as a pure function so it is unit-testable without rendering:
 * the first `CELL_COUNT` metrics remain stable in the headline row. Supporting metrics stay in the
 * expandable second row, including while one of them is selected, so the two layers never reshuffle.
 */
export function selectMetricStripCells(
  metrics: ReadonlyArray<AnalyticsMetricResult>,
  cellCount = CELL_COUNT,
): { cells: Array<AnalyticsMetricResult>; overflow: Array<AnalyticsMetricResult> } {
  const sorted = [...metrics].sort((a, b) => a.rank - b.rank);
  return { cells: sorted.slice(0, cellCount), overflow: sorted.slice(cellCount) };
}

/** Fixed overview strips never reveal a second metric row, even if a caller accidentally supplies extras. */
export function metricStripRows(
  metrics: ReadonlyArray<AnalyticsMetricResult>,
  allowOverflow = true,
  cellCount = CELL_COUNT,
): { cells: Array<AnalyticsMetricResult>; overflow: Array<AnalyticsMetricResult> } {
  const rows = selectMetricStripCells(metrics, cellCount);
  return allowOverflow ? rows : { ...rows, overflow: [] };
}

/**
 * The Commercial page's headline strip: one Compass White surface, six equal cells separated by
 * hairline dividers, never per-cell boxes (00_DESIGN_DIRECTION.md row 4). Cells are the metrics
 * ranked 1..6. Supporting metrics are available in a second row which stays collapsed until the
 * reader asks for it. This keeps the normal scan compact while making the full metric set visible
 * and comparable without a menu.
 */
export default function MetricStrip({
  metrics,
  sparklineTrend,
  focusMetric,
  focusVisible = true,
  onFocusChange,
  comparisonLabel,
  onAsk,
  onOpenDetails,
  onStartLens,
  allowOverflow = true,
  headlineCount = CELL_COUNT,
  loading = false,
  footer,
  panel,
}: MetricStripProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [showMoreMetrics, setShowMoreMetrics] = useState(false);
  const secondRowId = useId();
  const { cells, overflow } = metricStripRows(metrics, allowOverflow, headlineCount);
  const subjectInSecondRow = focusVisible && overflow.some((metric) => metric.id === focusMetric);
  const { open: secondRowOpen, disabled: moreDisabled } = moreMetricsState({
    subjectInSecondRow,
    userOpen: showMoreMetrics,
  });
  // Closing Explore must leave the row open, so a second-row subject is remembered as the user's choice.
  useEffect(() => {
    if (subjectInSecondRow) setShowMoreMetrics(true);
  }, [subjectInSecondRow]);

  if (loading) {
    return (
      <Box sx={STRIP_CONTAINER_SX}>
        <Box sx={{ ...cardSx(tokens), overflow: 'hidden' }}>
          <Box sx={{ ...GRID_BLEED_SX, ...stripGridSx(headlineCount) }}>
            {Array.from({ length: headlineCount }).map((_, index) => (
              <SkeletonCell key={index} />
            ))}
          </Box>
          {footer ? (
            <Box aria-hidden sx={{ opacity: 0.5, pointerEvents: 'none' }}>
              {footer(null)}
            </Box>
          ) : null}
        </Box>
      </Box>
    );
  }

  const moreMetrics =
    overflow.length > 0 ? (
      <MoreMetricsToggle
        controls={secondRowId}
        count={overflow.length}
        disabled={moreDisabled}
        onToggle={() => setShowMoreMetrics(!secondRowOpen)}
        open={secondRowOpen}
      />
    ) : null;

  const renderCell = (metric: AnalyticsMetricResult) => (
    <MetricCell
      key={metric.id}
      comparisonLabel={comparisonLabel}
      metric={metric}
      onAsk={() => onAsk(metric.id as CommercialMetricId)}
      onOpenDetails={() => onOpenDetails(metric.id as CommercialMetricId)}
      onSelect={() => onFocusChange(metric.id as CommercialMetricId)}
      onStartLens={() => onStartLens(metric.id as CommercialMetricId)}
      selected={focusVisible && metric.id === focusMetric}
      trend={sparklineTrend}
    />
  );

  return (
    <Box sx={STRIP_CONTAINER_SX}>
      <Box sx={{ ...cardSx(tokens), overflow: 'hidden', position: 'relative' }}>
        <Box sx={{ ...GRID_BLEED_SX, ...stripGridSx(headlineCount) }}>{cells.map(renderCell)}</Box>
        {overflow.length > 0 && (
          <Collapse id={secondRowId} in={secondRowOpen} timeout={reducedMotion ? 0 : undefined} unmountOnExit>
            <Box sx={{ ...GRID_BLEED_SX, ...stripGridSx(headlineCount) }}>{overflow.map(renderCell)}</Box>
          </Collapse>
        )}
        {footer?.(moreMetrics)}
        {panel}
      </Box>
    </Box>
  );
}

type MetricCellProps = {
  metric: AnalyticsMetricResult;
  trend: SparklineTrend | undefined;
  selected: boolean;
  comparisonLabel: string;
  onSelect: () => void;
  onAsk: () => void;
  onOpenDetails: () => void;
  onStartLens: () => void;
};

function MetricCell({
  metric,
  trend,
  selected,
  comparisonLabel,
  onSelect,
  onAsk,
  onOpenDetails,
  onStartLens,
}: MetricCellProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const id = metric.id as CommercialMetricId;
  const fit = useAreaFit();
  const fitted = fit >= 3;
  // The full design (14 16 12 padding, 9px above the sparkline) only at level 0; the fit levels keep the tuned, tighter tile.
  const roomy = fit === 0;
  const showSparkline = fit < 4;
  const sparklineHeight = fitted ? FITTED_SPARKLINE_HEIGHT_PX : roomy ? ROOMY_SPARKLINE_HEIGHT_PX : SPARKLINE_HEIGHT_PX;
  const unavailable = metric.status === 'unavailable';
  const value = formatMetric(id, unavailable ? null : (metric.value ?? null));
  const delta = unavailable ? null : formatMetricDelta(id, metric);
  const favourable = isFavourable(id, unavailable ? null : (metric.percentChange ?? null));
  const mutedColor = alpha(theme.palette.text.primary, 0.55);
  const deltaColor = !favourable ? mutedColor : tokens.accent;
  const requestedWindow = trend?.rollingWindowDays ?? 1;
  const sparkline = !unavailable && trend ? metricSparklineSeries(trend.points, id, requestedWindow) : null;
  const sparklineContext = trend ? sparklineContextLabel(trend) : '';

  // Whether the pointer or keyboard focus is anywhere in this cell (including its own nested
  // "Ask"/"Lens" buttons): drives a real conditional render of the "vs last year" row vs. the
  // actions row below, rather than a CSS opacity cross-fade. A `:hover`/`:focus-visible` opacity
  // swap left a window where both rows were visible at once (surfaced as the actions rendering on
  // top of "8.9% vs last year" after a click set lingering, non-keyboard focus), because the two
  // rows are two separate elements whose fades are not guaranteed to complete in lockstep. Mutually
  // exclusive rendering makes that overlap impossible by construction. `onFocus`/`onBlur` bubble
  // from the nested buttons via React's native `focusin`/`focusout` handling, so this behaves like
  // `:focus-within`, not just `:focus` on the cell's own root.
  const [active, setActive] = useState(false);

  return (
    <ButtonBase
      // Renders as a div, not a button: this cell contains its own nested "Ask"/"Lens"
      // action buttons (below), and a <button> cannot legally contain another <button>
      // (confirmed as a live React hydration-nesting warning). role="button" and an
      // explicit tabIndex keep it keyboard-focusable and Enter/Space-activatable via
      // ButtonBase's own key handling, which does not depend on the rendered tag.
      component="div"
      role="button"
      tabIndex={0}
      aria-label={`Select ${analyticsTitle(metric.shortName || metric.name)} to update the analysis. Current value: ${value}.`}
      aria-pressed={selected}
      onBlur={(event) => {
        // Moving focus from the cell root into Details/Ask/Lens is still focus-within. Do not
        // unmount the action row between pointer-down and click; that made the first action click
        // disappear without firing for keyboard and automated users alike.
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setActive(false);
        }
      }}
      onClick={onSelect}
      onFocus={() => setActive(true)}
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      sx={{
        alignItems: 'stretch',
        backgroundColor: selected ? alpha(tokens.accent, 0.022) : tokens.surface,
        borderTop: selected ? `3px solid ${tokens.accent}` : '3px solid transparent',
        boxShadow: `inset -1px 0 0 ${stripDividerColor(tokens)}, inset 0 -1px 0 ${stripDividerColor(tokens)}`,
        containerType: 'inline-size',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        minWidth: 0,
        p: fitted ? '8px 12px' : roomy ? '14px 16px 12px' : { xs: 1.25, md: 1.5 },
        position: 'relative',
        textAlign: 'left',
        transition: 'background-color 140ms ease',
        '&:hover': { backgroundColor: alpha(tokens.accent, 0.035) },
        '&:focus-visible': { outline: `2px solid ${tokens.accent}`, outlineOffset: -2 },
      }}
    >
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.75 }}>
        {selected && (
          <Box sx={{ backgroundColor: tokens.coralAccent, borderRadius: '50%', flexShrink: 0, height: 6, width: 6 }} />
        )}
        <Typography
          noWrap
          title={analyticsTitle(metric.shortName || metric.name)}
          sx={{
            color: alpha(theme.palette.text.primary, 0.75),
            fontFamily: FONT_STACK_SUISSE_INTL,
            fontSize: 11.5,
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          {metricCellLabel(metric)}
        </Typography>
      </Box>

      <Typography
        sx={{
          color: unavailable ? mutedColor : theme.palette.text.primary,
          fontFamily: FONT_STACK_SUISSE_WORKS,
          // The figure must never truncate (was ellipsis-clipping "$2,468.61" by about a pixel at
          // 1440): no textOverflow/ellipsis, overflow explicitly visible, and the size itself steps
          // down by the formatted string's length rather than relying on a fixed size to always fit.
          fontSize: valueFontSize(value, false),
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.1,
          overflow: 'visible',
          textOverflow: 'unset',
          whiteSpace: 'nowrap',
          [`@container (max-width: ${COMPACT_TILE_WIDTH}px)`]: { fontSize: valueFontSize(value, true) },
        }}
      >
        {value}
      </Typography>

      {unavailable ? (
        showSparkline ? (
          <Box sx={{ height: sparklineHeight }} />
        ) : null
      ) : (
        <>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: active ? 1.5 : 0.5,
              height: 16,
              mb: roomy ? '7px' : 0,
              minWidth: 0,
            }}
          >
            {active ? (
              <>
                <ButtonBase
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenDetails();
                  }}
                  sx={{ color: tokens.accent, fontFamily: FONT_STACK_SUISSE_INTL, fontSize: 13, fontWeight: 600 }}
                >
                  Details
                </ButtonBase>
                <ButtonBase
                  onClick={(event) => {
                    event.stopPropagation();
                    onAsk();
                  }}
                  sx={{ color: tokens.accent, fontFamily: FONT_STACK_SUISSE_INTL, fontSize: 13, fontWeight: 600 }}
                >
                  Ask
                </ButtonBase>
                <ButtonBase
                  onClick={(event) => {
                    event.stopPropagation();
                    onStartLens();
                  }}
                  sx={{ color: tokens.accent, fontFamily: FONT_STACK_SUISSE_INTL, fontSize: 13, fontWeight: 600 }}
                >
                  Lens
                </ButtonBase>
              </>
            ) : (
              <Box
                sx={{
                  alignItems: 'center',
                  color: deltaColor,
                  display: 'flex',
                  fontFamily: FONT_STACK_SUISSE_INTL,
                  fontSize: 13.5,
                  fontVariantNumeric: 'tabular-nums',
                  gap: 0.5,
                  minWidth: 0,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {delta && <span>{delta}</span>}
                <Box
                  component="span"
                  sx={{ color: alpha(theme.palette.text.primary, 0.6), overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {comparisonLabel}
                </Box>
              </Box>
            )}
          </Box>
          {showSparkline ? (
            <Tooltip placement="bottom" title={sparklineContext}>
              <Box
                aria-label={`${analyticsTitle(metric.shortName || metric.name)} trend · ${sparklineContext}`}
                role="img"
              >
                <Sparkline
                  height={sparklineHeight}
                  smoothWindow={sparkline?.smoothWindow ?? 1}
                  values={sparkline?.values ?? []}
                />
              </Box>
            </Tooltip>
          ) : null}
        </>
      )}
    </ButtonBase>
  );
}

function SkeletonCell() {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  return (
    <Box
      sx={{
        backgroundColor: tokens.surface,
        boxShadow: `inset -1px 0 0 ${stripDividerColor(tokens)}, inset 0 -1px 0 ${stripDividerColor(tokens)}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        minWidth: 0,
        p: 1.5,
      }}
    >
      <Skeleton height={14} sx={{ bgcolor: alpha(tokens.accent, 0.08) }} variant="text" width="60%" />
      <Skeleton height={34} sx={{ bgcolor: alpha(tokens.accent, 0.08) }} variant="text" width="80%" />
      <Skeleton height={16} sx={{ bgcolor: alpha(tokens.accent, 0.08) }} variant="text" width="50%" />
      <Skeleton height={32} sx={{ bgcolor: alpha(tokens.accent, 0.06) }} variant="rounded" width="100%" />
    </Box>
  );
}
